'use client';

import React, { useState, useRef, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  Chip,
  IconButton,
  Paper,
  Divider,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Instagram,
  Facebook,
  Send,
  ExpandMore,
  AutoAwesome,
  Preview,
  CloudUpload,
  DeleteOutline,
  Edit,
  Settings,
  Add,
} from '@mui/icons-material';

// Custom Threads Icon Component - 실제 Threads 공식 아이콘
const ThreadsIcon = ({ sx, ...props }: { sx?: any; [key: string]: any }) => (
  <svg
    aria-label='Threads'
    fill='currentColor'
    height='24'
    role='img'
    viewBox='0 0 192 192'
    width='24'
    style={{ ...sx }}
    {...props}
  >
    <path d='M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z' />
  </svg>
);
import { toast } from 'sonner';
import { Review } from '../api/review.api';
import { SocialPlatform } from '../api/social.api';
import ReviewSelector from './ReviewSelector';
import Config from '../config/constants';

interface BlogEditorProps {
  branchId: number;
  reviews: Review[];
  onPost: (
    content: string,
    hashtags: string[],
    selectedReviews: number[],
    uploadedImages: string[],
  ) => void;
  onAIGenerate: (
    selectedReviews: Review[],
    uploadedImages: string[],
    currentText: string,
    allSelectedImages: string[],
  ) => Promise<string>;
  socialAccounts: any[];
}

interface PostContent {
  text: string;
  images: string[];
  hashtags: string[];
}

// 날짜 포맷팅 함수
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 텍스트에서 해시태그를 추출하는 함수
 * @param text - 해시태그가 포함된 텍스트
 * @returns 추출된 해시태그 배열
 */
const extractHashtags = (text: string): string[] => {
  const hashtagRegex = /#[a-zA-Z0-9가-힣_]+/g;
  const matches = text.match(hashtagRegex);
  return matches ?  Array.from(new Set(matches)) : []; // 중복 제거
};

/**
 * 텍스트에서 해시태그를 제거하는 함수
 * @param text - 해시태그가 포함된 텍스트
 * @returns 해시태그가 제거된 텍스트
 */
const removeHashtags = (text: string): string => {
  const hashtagRegex = /#[a-zA-Z0-9가-힣_]+/g;
  
  // 해시태그 제거
  let cleanedText = text.replace(hashtagRegex, '');
  
  // 줄바꿈을 임시로 보호
  cleanedText = cleanedText.replace(/\n/g, '{{NEWLINE}}');
  
  // 연속된 공백을 하나로 변환 (줄바꿈 제외)
  cleanedText = cleanedText.replace(/[ \t]+/g, ' ');
  
  // 줄바꿈 복원
  cleanedText = cleanedText.replace(/{{NEWLINE}}/g, '\n');
  
  // 연속된 빈 줄을 하나로 정리 (최대 2개까지만 허용)
  cleanedText = cleanedText.replace(/\n\s*\n\s*\n+/g, '\n\n');
  
  // 앞뒤 공백 제거
  cleanedText = cleanedText.trim();
  
  // 텍스트 끝에 불필요한 구두점이나 공백이 있으면 정리
  cleanedText = cleanedText.replace(/[,\s]*$/, '');
  
  return cleanedText;
};

const BlogEditor: React.FC<BlogEditorProps> = ({
  branchId,
  reviews,
  onPost,
  onAIGenerate,
  socialAccounts,
}) => {
  const [postContent, setPostContent] = useState<PostContent>({
    text: '',
    images: [],
    hashtags: [],
  });

  const [selectedReviewImages, setSelectedReviewImages] = useState<{
    [reviewId: number]: string[];
  }>({});
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<
    (SocialPlatform | 'meta-integration' | 'threads')[]
  >([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // ReviewSelector 관련 상태
  const [selectedReviews, setSelectedReviews] = useState<number[]>([]);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  
  // 실제 리뷰 데이터 (ReviewSelector에서 로드된 최신 데이터)
  const [loadedReviews, setLoadedReviews] = useState<Review[]>(reviews);

  // 설정 관련 상태
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [requiredHashtags, setRequiredHashtags] = useState<string[]>([]);
  const [useRequiredHashtags, setUseRequiredHashtags] = useState(false);
  const [prefixText, setPrefixText] = useState('');
  const [suffixText, setSuffixText] = useState('');
  const [usePrefixSuffix, setUsePrefixSuffix] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isMobile = useMediaQuery('(max-width:600px)');

  /**
   * localStorage에서 설정 데이터를 로드하는 useEffect
   */
  useEffect(() => {
    // 필수 해시태그 설정 로드
    const savedRequiredHashtags = localStorage.getItem(`requiredHashtags_${branchId}`);
    const savedUseRequiredHashtags = localStorage.getItem(`useRequiredHashtags_${branchId}`);
    
    if (savedRequiredHashtags) {
      try {
        setRequiredHashtags(JSON.parse(savedRequiredHashtags));
      } catch (error) {
        console.error('필수 해시태그 로드 실패:', error);
      }
    }
    
    if (savedUseRequiredHashtags) {
      setUseRequiredHashtags(savedUseRequiredHashtags === 'true');
    }

    // 전후 고정 문구 설정 로드
    const savedPrefixText = localStorage.getItem(`prefixText_${branchId}`);
    const savedSuffixText = localStorage.getItem(`suffixText_${branchId}`);
    const savedUsePrefixSuffix = localStorage.getItem(`usePrefixSuffix_${branchId}`);

    if (savedPrefixText) {
      setPrefixText(savedPrefixText);
    }
    
    if (savedSuffixText) {
      setSuffixText(savedSuffixText);
    }
    
    if (savedUsePrefixSuffix) {
      setUsePrefixSuffix(savedUsePrefixSuffix === 'true');
    }

    console.log('📋 설정 로드 완료:', {
      requiredHashtags: savedRequiredHashtags ? JSON.parse(savedRequiredHashtags) : [],
      useRequiredHashtags: savedUseRequiredHashtags === 'true',
      prefixText: savedPrefixText || '',
      suffixText: savedSuffixText || '',
      usePrefixSuffix: savedUsePrefixSuffix === 'true'
    });
  }, [branchId]);

  // 이미지 업로드 핸들러
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files) return;

    setImageUploading(true);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          console.log('📤 이미지 업로드 시작:', file.name);

          // S3에 이미지 업로드
          const formData = new FormData();
          formData.append('file', file);

          const response = await fetch(
            `${Config.API_HOST}/api/app/review/upload`,
            {
              method: 'POST',
              body: formData,
            },
          );

          if (!response.ok) {
            throw new Error(`업로드 실패: ${response.status}`);
          }

          const result = await response.json();
          console.log('✅ 이미지 업로드 성공:', result.data.imageUrl);

          return result.data.imageUrl;
        } catch (error) {
          console.error('❌ 이미지 업로드 실패:', file.name, error);
          toast.error(`${file.name} 업로드에 실패했습니다.`);
          return null;
        }
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const successUrls = uploadedUrls.filter(
        (url) => url !== null,
      ) as string[];

      if (successUrls.length > 0) {
        setUploadedImages((prev) => [...prev, ...successUrls]);
        setPostContent((prev) => ({
          ...prev,
          images: [...prev.images, ...successUrls],
        }));

        toast.success(`${successUrls.length}개 이미지 업로드 완료!`);
      }
    } catch (error) {
      console.error('이미지 업로드 오류:', error);
      toast.error('이미지 업로드에 실패했습니다.');
    } finally {
      setImageUploading(false);
    }
  };

  // 리뷰 이미지 선택 핸들러
  const handleReviewImageSelect = (reviewId: number, imageUrl: string) => {
    setSelectedReviewImages((prev) => {
      const current = prev[reviewId] || [];
      const isSelected = current.includes(imageUrl);

      if (isSelected) {
        const updated = current.filter((url) => url !== imageUrl);
        setPostContent((prevContent) => ({
          ...prevContent,
          images: prevContent.images.filter((img) => img !== imageUrl),
        }));
        return { ...prev, [reviewId]: updated };
      } else {
        const updated = [...current, imageUrl];
        setPostContent((prevContent) => ({
          ...prevContent,
          images: [...prevContent.images, imageUrl],
        }));
        return { ...prev, [reviewId]: updated };
      }
    });
  };

  // ReviewSelector 핸들러
  const handleReviewSelect = (reviewId: number) => {
    setSelectedReviews((prev) =>
      prev.includes(reviewId)
        ? prev.filter((id) => id !== reviewId)
        : [...prev, reviewId],
    );
  };

  const handleImageSelect = (imageUrl: string) => {
    setSelectedImages((prev) =>
      prev.includes(imageUrl)
        ? prev.filter((url) => url !== imageUrl)
        : [...prev, imageUrl],
    );

    // 기존 postContent.images와 동기화
    setPostContent((prev) => ({
      ...prev,
      images: selectedImages.includes(imageUrl)
        ? prev.images.filter((img) => img !== imageUrl)
        : [...prev.images, imageUrl],
    }));
  };

  // ReviewSelector에서 리뷰 데이터 로드 시 호출되는 콜백
  const handleReviewsLoad = (reviewsData: Review[]) => {
    setLoadedReviews(reviewsData);
    console.log('📋 리뷰 데이터 업데이트:', {
      count: reviewsData.length,
      reviews: reviewsData.map(r => ({ id: r.id, text: r.reviewText?.substring(0, 50) }))
    });
  };

  // AI 포스팅 생성
  const handleAIGenerate = async () => {
    setAiGenerating(true);

    try {
      // 업로드된 이미지 + 선택된 리뷰 이미지를 모두 합침
      const allSelectedImages = [...uploadedImages, ...selectedImages];

      // 선택된 리뷰들 필터링 (최신 리뷰 데이터 사용)
      const selectedReviewData =
        selectedReviews.length > 0
          ? loadedReviews.filter((review) => selectedReviews.includes(review.id))
          : []; // 선택된 리뷰가 없으면 빈 배열 사용

      // 저장된 사용자 가이드라인과 Instagram URL 가져오기
      const customGuideline =
        localStorage.getItem(`customGuideline_${branchId}`) || '';
      const instagramUrl =
        localStorage.getItem(`instagramUrl_${branchId}`) || '';

      console.log('🤖 AI 생성 요청:', {
        selectedReviews: selectedReviewData.length,
        selectedReviewData: selectedReviewData.map(r => ({
          id: r.id,
          serviceType: r.serviceType,
          reviewText: r.reviewText?.substring(0, 100),
          menu: r.menu,
          customerName: r.customerName,
        })),
        uploadedImages: uploadedImages.length,
        reviewImages: selectedImages.length,
        totalImages: allSelectedImages.length,
        customGuideline: customGuideline ? '설정됨' : '없음',
        instagramUrl: instagramUrl ? '설정됨' : '없음',
        currentText: postContent.text.substring(0, 50) + '...',
      });

      // 가이드라인 추가 정보 포함
      let enhancedCurrentText = postContent.text;
      if (customGuideline) {
        enhancedCurrentText += `\n\n[사용자 가이드라인: ${customGuideline}]`;
      }
      if (instagramUrl) {
        enhancedCurrentText += `\n\n[참고 Instagram 스타일: ${instagramUrl}]`;
      }

      const generatedContent = await onAIGenerate(
        selectedReviewData,
        uploadedImages,
        enhancedCurrentText,
        allSelectedImages,
      );

      // 생성된 텍스트에서 해시태그 추출
      const extractedHashtags = extractHashtags(generatedContent);
      
      console.log('🏷️ AI 생성 텍스트에서 추출된 해시태그:', extractedHashtags);

      // 텍스트에서 해시태그 제거 (해시태그는 별도 섹션에서 관리)
      const cleanedText = removeHashtags(generatedContent);
      
      console.log('✂️ 해시태그 제거 후 텍스트:', {
        originalLength: generatedContent.length,
        cleanedLength: cleanedText.length,
        removedHashtags: extractedHashtags.length
      });

      // 텍스트 업데이트 (해시태그가 제거된 깔끔한 텍스트)
      // AI 응답에서 온 줄바꿈을 실제 줄바꿈으로 변환
      let formattedText = cleanedText;
      
      console.log('🔍 원본 텍스트 분석:', {
        text: cleanedText,
        length: cleanedText.length,
        hasBackslashN: cleanedText.includes('\\n'),
        hasNewline: cleanedText.includes('\n'),
        charCodes: cleanedText.split('').slice(0, 20).map(c => `${c}(${c.charCodeAt(0)})`).join(' ')
      });
      
      // 여러 형태의 줄바꿈 처리
      // 1. 문자열 "\\n" (백슬래시 두 개)를 실제 줄바꿈으로
      if (formattedText.includes('\\n')) {
        formattedText = formattedText.replace(/\\n/g, '\n');
        console.log('✅ \\n을 실제 줄바꿈으로 변환함');
      }
      
      // 2. 이미 실제 줄바꿈이 있다면 그대로 유지
      
      // 3. 전후 고정 문구 적용
      if (usePrefixSuffix) {
        let finalText = formattedText;
        
        if (prefixText.trim()) {
          finalText = prefixText.trim() + '\n\n' + finalText;
          console.log('✅ 앞 고정 문구 적용:', prefixText.trim());
        }
        
        if (suffixText.trim()) {
          finalText = finalText + '\n\n' + suffixText.trim();
          console.log('✅ 뒤 고정 문구 적용:', suffixText.trim());
        }
        
        formattedText = finalText;
      }
      
      console.log('🔄 변환 후 텍스트:', {
        text: formattedText,
        lines: formattedText.split('\n').length,
        preview: formattedText.substring(0, 200) + '...'
      });
      
      setPostContent((prev) => ({ ...prev, text: formattedText }));

      // 추출된 해시태그와 필수 해시태그를 해시태그 섹션에 추가
      setPostContent((prev) => {
        const newHashtags = [...prev.hashtags];
        let addedCount = 0;
        
        // 1. AI에서 추출된 해시태그 추가
        extractedHashtags.forEach(hashtag => {
          if (!newHashtags.includes(hashtag)) {
            newHashtags.push(hashtag);
            addedCount++;
          }
        });
        
        // 2. 필수 해시태그 추가 (설정이 활성화된 경우)
        if (useRequiredHashtags && requiredHashtags.length > 0) {
          requiredHashtags.forEach(hashtag => {
            if (!newHashtags.includes(hashtag)) {
              newHashtags.push(hashtag);
              addedCount++;
            }
          });
          console.log('✅ 필수 해시태그 자동 추가:', requiredHashtags);
        }
        
        console.log('✅ 해시태그 섹션에 추가:', {
          extracted: extractedHashtags,
          required: useRequiredHashtags ? requiredHashtags : [],
          added: addedCount,
          total: newHashtags.length
        });
        
        return {
          ...prev,
          hashtags: newHashtags
        };
      });

      // 성공 메시지에 설정된 가이드라인 반영 여부 표시
      let successMessage = 'AI 포스팅 생성이 완료되었습니다! 🎉';
      
      // 가이드라인 반영 메시지
      if (customGuideline || instagramUrl) {
        successMessage += ' 💡 설정하신 가이드라인이 반영되었습니다.';
      }
      
      // 전후 고정 문구 적용 메시지
      if (usePrefixSuffix && (prefixText.trim() || suffixText.trim())) {
        successMessage += ' 📝 고정 문구가 적용되었습니다.';
      }
      
      // 해시태그 추가 메시지
      const totalNewHashtags = extractedHashtags.length + (useRequiredHashtags ? requiredHashtags.length : 0);
      if (totalNewHashtags > 0) {
        successMessage += ` 🏷️ ${totalNewHashtags}개의 해시태그가 자동으로 추가되었습니다.`;
      }
      
      toast.success(successMessage);
    } catch (error) {
      console.error('AI 생성 오류:', error);
      toast.error('AI 포스팅 생성에 실패했습니다.');
    } finally {
      setAiGenerating(false);
    }
  };

  // 해시태그 관리
  const handleHashtagAdd = (hashtag: string) => {
    if (hashtag && !postContent.hashtags.includes(hashtag)) {
      setPostContent((prev) => ({
        ...prev,
        hashtags: [...prev.hashtags, hashtag],
      }));
    }
  };

  const handleHashtagRemove = (hashtag: string) => {
    setPostContent((prev) => ({
      ...prev,
      hashtags: prev.hashtags.filter((h) => h !== hashtag),
    }));
  };

  /**
   * 필수 해시태그 추가 핸들러
   */
  const handleRequiredHashtagAdd = (hashtag: string) => {
    if (hashtag && !requiredHashtags.includes(hashtag)) {
      const newHashtags = [...requiredHashtags, hashtag];
      setRequiredHashtags(newHashtags);
      // localStorage에 저장
      localStorage.setItem(`requiredHashtags_${branchId}`, JSON.stringify(newHashtags));
      console.log('✅ 필수 해시태그 추가:', hashtag);
    }
  };

  /**
   * 필수 해시태그 제거 핸들러
   */
  const handleRequiredHashtagRemove = (hashtag: string) => {
    const newHashtags = requiredHashtags.filter((h) => h !== hashtag);
    setRequiredHashtags(newHashtags);
    // localStorage에 저장
    localStorage.setItem(`requiredHashtags_${branchId}`, JSON.stringify(newHashtags));
    console.log('❌ 필수 해시태그 제거:', hashtag);
  };

  /**
   * 필수 해시태그 사용 여부 토글
   */
  const handleUseRequiredHashtagsToggle = (checked: boolean) => {
    setUseRequiredHashtags(checked);
    localStorage.setItem(`useRequiredHashtags_${branchId}`, checked.toString());
    console.log('🔄 필수 해시태그 사용 설정:', checked);
  };

  /**
   * 전후 고정 문구 설정 저장
   */
  const handlePrefixSuffixSave = () => {
    localStorage.setItem(`prefixText_${branchId}`, prefixText);
    localStorage.setItem(`suffixText_${branchId}`, suffixText);
    localStorage.setItem(`usePrefixSuffix_${branchId}`, usePrefixSuffix.toString());
    console.log('💾 전후 고정 문구 설정 저장:', {
      prefixText,
      suffixText,
      usePrefixSuffix
    });
  };

  /**
   * 필수 해시태그를 현재 포스팅에 적용
   */
  const applyRequiredHashtags = () => {
    if (useRequiredHashtags && requiredHashtags.length > 0) {
      const newHashtags = [...postContent.hashtags];
      let addedCount = 0;
      
      requiredHashtags.forEach(hashtag => {
        if (!newHashtags.includes(hashtag)) {
          newHashtags.push(hashtag);
          addedCount++;
        }
      });
      
      if (addedCount > 0) {
        setPostContent(prev => ({
          ...prev,
          hashtags: newHashtags
        }));
        console.log(`✅ 필수 해시태그 ${addedCount}개 적용됨`);
      }
    }
  };

  // 포스팅 실행
  const handlePost = () => {
    if (!postContent.text.trim()) {
      toast.error('포스팅 내용을 입력해주세요.');
      return;
    }

    setPosting(true);

    try {
      // 업로드된 이미지 + 선택된 리뷰 이미지를 모두 합침
      const allSelectedImages = [...uploadedImages, ...selectedImages];

      console.log('📷 포스팅에 사용될 이미지:', {
        uploadedImages: uploadedImages.length,
        reviewImages: selectedImages.length,
        total: allSelectedImages.length,
        images: allSelectedImages,
      });

      console.log('📝 선택된 리뷰:', {
        reviewIds: selectedReviews,
        totalReviews: loadedReviews.length,
        selectedReviewData: selectedReviews.map(id => {
          const review = loadedReviews.find(r => r.id === id);
          return { id, reviewText: review?.reviewText?.substring(0, 50) };
        }),
      });

      // 이미지가 하나도 없으면 경고
      if (allSelectedImages.length === 0) {
        toast.warning('포스팅에 포함할 이미지를 선택하거나 업로드해주세요.');
        return;
      }

      onPost(
        postContent.text,
        postContent.hashtags,
        selectedReviews, // 선택된 리뷰 ID들
        allSelectedImages,
      );

      // 성공 후 초기화
      setPostContent({
        text: '',
        images: [],
        hashtags: ['#맛집', '#리뷰', '#추천'],
      });
      setSelectedReviews([]);
      setSelectedImages([]);
      setUploadedImages([]);
      setSelectedPlatforms([]);
      setPreviewOpen(false);
      toast.success('포스팅을 업로드 중입니다.');
    } catch (error) {
      console.error('포스팅 오류:', error);
      toast.error('포스팅에 실패했습니다.');
    } finally {
      setPosting(false);
    }
  };

  // 플랫폼 선택
  const handlePlatformToggle = (
    platform: SocialPlatform | 'meta-integration' | 'threads',
  ) => {
    setSelectedPlatforms((prev) => {
      if (prev.includes(platform)) {
        return prev.filter((p) => p !== platform);
      } else {
        return [...prev, platform];
      }
    });
  };

  // 연결된 계정 확인
  const isAccountConnected = (platform: string) => {
    return socialAccounts.some(
      (account) =>
        (platform === 'meta-integration' &&
          account.platform === SocialPlatform.FACEBOOK &&
          account.isConnected) ||
        (platform === 'threads' &&
          account.platform === SocialPlatform.THREADS &&
          account.isConnected),
    );
  };

  return (
    <Box sx={{ mx: 'auto', p: isMobile ? 2 : 3 }}>
      {/* 헤더 */}

      <Grid container spacing={isMobile ? 2 : 4}>
        {/* 왼쪽: 에디터 영역 */}
        <Grid item xs={12} lg={8}>
          {/* 텍스트 에디터 */}
          <Card sx={{ mb: isMobile ? 2 : 3, borderRadius: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0,
                }}
              >
                <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='700'>
                  📝 포스팅 내용
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                  <Button
                    variant='outlined'
                    size={isMobile ? 'small' : 'small'}
                    startIcon={<Preview />}
                    onClick={() => setPreviewOpen(true)}
                    sx={{ 
                      borderRadius: 2, 
                      color: '#000',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      flex: isMobile ? 1 : 'none',
                    }}
                  >
                    미리보기
                  </Button>
                  <Button
                    variant='contained'
                    size={isMobile ? 'small' : 'small'}
                    startIcon={
                      aiGenerating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AutoAwesome />
                      )
                    }
                    onClick={handleAIGenerate}
                    disabled={aiGenerating}
                    sx={{
                      borderRadius: 2,
                      color: '#fff',
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      flex: isMobile ? 1 : 'none',
                    }}
                  >
                    {aiGenerating ? 'AI 생성 중...' : '🤖 AI 포스팅 생성'}
                  </Button>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={isMobile ? 6 : 8}
                value={postContent.text}
                onChange={(e) =>
                  setPostContent((prev) => ({ ...prev, text: e.target.value }))
                }
                placeholder='여기에 포스팅 내용을 작성하거나 AI 생성 버튼을 클릭하세요...'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    '& fieldset': {
                      borderWidth: 2,
                    },
                    '& textarea': {
                      whiteSpace: 'pre-wrap !important',
                      lineHeight: '1.6 !important',
                    },
                    '& .MuiInputBase-input': {
                      whiteSpace: 'pre-wrap !important',
                    },
                  },
                }}
              />

              <Box
                sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
              >
                <Typography variant='body2' color='text.secondary' sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                  {postContent.text.length} / 2000자
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {/* 가이드라인 설정 상태 표시 */}
                {(() => {
                  const customGuideline = localStorage.getItem(
                    `customGuideline_${branchId}`,
                  );
                  const instagramUrl = localStorage.getItem(
                    `instagramUrl_${branchId}`,
                  );

                  if (customGuideline || instagramUrl) {
                    return (
                      <Chip
                        size='small'
                        icon={<AutoAwesome />}
                        label={`맞춤 설정 활성화 (${[customGuideline && '가이드라인', instagramUrl && 'Instagram 참고'].filter(Boolean).join(', ')})`}
                        color='primary'
                        variant='outlined'
                        sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                      />
                    );
                  }

                  return (
                    <Typography variant='body2' color='text.secondary' sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                      💡 AI가 리뷰 내용과 이미지를 분석하여 매력적인 포스팅을
                      생성합니다
                    </Typography>
                  );
                })()}
              </Box>
            </CardContent>
          </Card>

          {/* 이미지 관리 */}
          <Card sx={{ mb: isMobile ? 2 : 3, borderRadius: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='700' sx={{ mb: 2 }}>
                📷 이미지 관리
              </Typography>

              {/* 선택된 모든 이미지 미리보기 */}
              {(() => {
                const selectedReviewImageUrls: string[] = [];
                Object.values(selectedReviewImages).forEach((images) => {
                  selectedReviewImageUrls.push(...images);
                });
                const allSelectedImages = [
                  ...uploadedImages,
                  ...selectedReviewImageUrls,
                ];

                return (
                  allSelectedImages.length > 0 && (
                    <Box
                      sx={{
                        mb: 3,
                        p: isMobile ? 1.5 : 2,
                        bgcolor: 'rgba(25, 118, 210, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(25, 118, 210, 0.2)',
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='600'
                        sx={{ mb: 1, color: '#1976d2', fontSize: isMobile ? '0.9rem' : '1rem' }}
                      >
                        📱 포스팅에 포함될 이미지 ({allSelectedImages.length}개)
                      </Typography>
                      <ImageList cols={isMobile ? 4 : 6} rowHeight={isMobile ? 60 : 80} sx={{ mb: 0 }}>
                        {allSelectedImages.map((image, index) => (
                          <ImageListItem key={index}>
                            <img
                              src={image}
                              alt={`선택된 이미지 ${index + 1}`}
                              loading='lazy'
                              style={{ borderRadius: 4 }}
                            />
                          </ImageListItem>
                        ))}
                      </ImageList>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mt: 1, display: 'block', fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      >
                        �� 업로드한 이미지와 선택한 리뷰 이미지가 모두
                        포함됩니다
                      </Typography>
                    </Box>
                  )
                );
              })()}

              {/* 이미지 업로드 */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}
                >
                  <Typography variant='subtitle1' fontWeight='600' sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                    새 이미지 업로드
                  </Typography>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={
                      imageUploading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CloudUpload />
                      )
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imageUploading}
                    sx={{ 
                      borderRadius: 2, 
                      color: '#000',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                    }}
                  >
                    {imageUploading ? '업로드 중...' : '파일 선택'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type='file'
                    multiple
                    accept='image/*'
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                </Box>

                {uploadedImages.length > 0 && (
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      업로드된 이미지 ({uploadedImages.length}개)
                    </Typography>
                    <ImageList cols={isMobile ? 2 : 4} rowHeight={isMobile ? 100 : 120} sx={{ mb: 2 }}>
                      {uploadedImages.map((image, index) => (
                        <ImageListItem key={index}>
                          <img
                            src={image}
                            alt={`업로드 ${index + 1}`}
                            loading='lazy'
                          />
                          <ImageListItemBar
                            actionIcon={
                              <IconButton
                                sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                onClick={() => {
                                  setUploadedImages((prev) =>
                                    prev.filter((_, i) => i !== index),
                                  );
                                  setPostContent((prev) => ({
                                    ...prev,
                                    images: prev.images.filter(
                                      (img) => img !== image,
                                    ),
                                  }));
                                }}
                              >
                                <DeleteOutline />
                              </IconButton>
                            }
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* 리뷰 이미지 갤러리 */}
              <Box>
                {/* 통합 리뷰 선택기 */}
                <ReviewSelector
                  branchId={branchId}
                  selectedReviews={selectedReviews}
                  selectedImages={selectedImages}
                  onReviewSelect={handleReviewSelect}
                  onImageSelect={handleImageSelect}
                  onReviewsLoad={handleReviewsLoad}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 오른쪽: 설정 및 미리보기 */}
        <Grid item xs={12} lg={4}>
          {/* 해시태그 관리 */}
          <Card sx={{ mb: isMobile ? 2 : 3, borderRadius: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='700'>
                  #️⃣ 해시태그
                </Typography>
                <Button
                  size='small'
                  startIcon={<Settings />}
                  onClick={() => setSettingsOpen(true)}
                  sx={{ 
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    minWidth: 'auto',
                    px: 1.5,
                  }}
                >
                  설정
                </Button>
              </Box>

              {/* 현재 포스팅 해시태그 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                  현재 포스팅 해시태그
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {postContent.hashtags.map((hashtag, index) => (
                    <Chip
                      key={index}
                      label={hashtag}
                      onDelete={() => handleHashtagRemove(hashtag)}
                      sx={{ 
                        borderRadius: 2,
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                      }}
                    />
                  ))}
                </Box>

                <TextField
                  fullWidth
                  size='small'
                  placeholder='#새해시태그 입력 후 Enter'
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const value = (e.target as HTMLInputElement).value.trim();
                      if (value) {
                        const hashtag = value.startsWith('#')
                          ? value
                          : `#${value}`;
                        handleHashtagAdd(hashtag);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                    },
                  }}
                />
              </Box>

              {/* 필수 해시태그 표시 */}
              {useRequiredHashtags && requiredHashtags.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(25, 118, 210, 0.05)', 
                  borderRadius: 2, 
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                  mb: 2 
                }}>
                  <Typography variant='subtitle2' fontWeight='600' sx={{ mb: 1, color: '#1976d2', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    ⭐ 필수 해시태그 (자동 포함)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {requiredHashtags.map((hashtag, index) => (
                      <Chip
                        key={index}
                        label={hashtag}
                        size='small'
                        color='primary'
                        variant='outlined'
                        sx={{ 
                          fontSize: isMobile ? '0.7rem' : '0.8rem',
                        }}
                      />
                    ))}
                  </Box>
                  <Button
                    size='small'
                    onClick={applyRequiredHashtags}
                    sx={{ 
                      mt: 1, 
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                    }}
                  >
                    필수 해시태그 적용
                  </Button>
                </Box>
              )}

              {/* 전후 고정 문구 표시 */}
              {usePrefixSuffix && (prefixText.trim() || suffixText.trim()) && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(76, 175, 80, 0.05)', 
                  borderRadius: 2, 
                  border: '1px solid rgba(76, 175, 80, 0.2)' 
                }}>
                  <Typography variant='subtitle2' fontWeight='600' sx={{ mb: 1, color: '#4caf50', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    📝 고정 문구 설정 (AI 생성 시 자동 적용)
                  </Typography>
                  {prefixText.trim() && (
                    <Typography variant='caption' sx={{ display: 'block', mb: 0.5, fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                      앞 문구: {prefixText.trim().substring(0, 30)}{prefixText.trim().length > 30 ? '...' : ''}
                    </Typography>
                  )}
                  {suffixText.trim() && (
                    <Typography variant='caption' sx={{ display: 'block', fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                      뒤 문구: {suffixText.trim().substring(0, 30)}{suffixText.trim().length > 30 ? '...' : ''}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* 포스팅 버튼 */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              {(() => {
                const selectedReviewImageUrls: string[] = [];
                Object.values(selectedReviewImages).forEach((images) => {
                  selectedReviewImageUrls.push(...images);
                });
                const allSelectedImages = [
                  ...uploadedImages,
                  ...selectedReviewImageUrls,
                ];

                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      📊 포스팅 요약
                    </Typography>
                    <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={`이미지 ${allSelectedImages.length}개`}
                        size='small'
                        variant='outlined'
                        color={
                          allSelectedImages.length > 0 ? 'success' : 'default'
                        }
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      />
                      <Chip
                        label={`플랫폼 ${selectedPlatforms.length}개`}
                        size='small'
                        variant='outlined'
                        color={
                          selectedPlatforms.length > 0 ? 'primary' : 'default'
                        }
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      />
                      <Chip
                        label={`해시태그 ${postContent.hashtags.length}개`}
                        size='small'
                        variant='outlined'
                        color={
                          postContent.hashtags.length > 0
                            ? 'secondary'
                            : 'default'
                        }
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      />
                    </Box>
                  </Box>
                );
              })()}

              <Button
                fullWidth
                variant='contained'
                size='large'
                startIcon={
                  posting ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : (
                    <Send />
                  )
                }
                onClick={handlePost}
               
                sx={{
                  py: isMobile ? 1.5 : 2,
                  borderRadius: 3,
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                    color: 'rgba(0,0,0,0.26)',
                  },
                }}
              >
                {posting ? '포스팅 중...' : '🚀 포스팅 발행하기'}
              </Button>

              <Typography
                  variant='caption'
                  color='error'
                  sx={{ mt: 1, display: 'block', textAlign: 'center', fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                >
                 실제 포스팅까지 3분에서 5분정도 소요됩니다.
                </Typography>

              {(postContent.text.trim() === '' ||
                selectedPlatforms.length === 0) && (
                <Typography
                  variant='caption'
                  color='error'
                  sx={{ mt: 1, display: 'block', textAlign: 'center', fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                >
                  {postContent.text.trim() === '' &&
                    '포스팅 내용을 입력해주세요'}
                  {postContent.text.trim() === '' &&
                    selectedPlatforms.length === 0 &&
                    ' • '}
                  {selectedPlatforms.length === 0 && '플랫폼을 선택해주세요'}
                </Typography>
              )}

              {postContent.text.trim() !== '' &&
                selectedPlatforms.length > 0 && (
                  <Typography
                    variant='caption'
                    color='success.main'
                    sx={{
                      mt: 1,
                      display: 'block',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                    }}
                  >
                    ✅ 포스팅 준비 완료!
                  </Typography>
                )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 미리보기 다이얼로그 */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: isMobile ? '90vh' : '80vh',
            m: isMobile ? 1 : 2,
          }
        }}
      >
        <DialogTitle>
          <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='700'>
            📱 포스팅 미리보기
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 3, bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }}>U</Avatar>
              <Box>
                <Typography variant='subtitle1' fontWeight='600' sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  Your Business Name
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                  방금 전
                </Typography>
              </Box>
            </Box>

            <Typography variant='body1' sx={{ mb: 2, whiteSpace: 'pre-wrap', fontSize: isMobile ? '0.9rem' : '1rem' }}>
              {postContent.text || '포스팅 내용이 여기에 표시됩니다...'}
            </Typography>

            {(() => {
              // 선택된 모든 이미지 수집
              const selectedReviewImageUrls: string[] = [];
              Object.values(selectedReviewImages).forEach((images) => {
                selectedReviewImageUrls.push(...images);
              });
              const allSelectedImages = [
                ...uploadedImages,
                ...selectedReviewImageUrls,
              ];

              return (
                allSelectedImages.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      첨부된 이미지 ({allSelectedImages.length}개)
                    </Typography>
                    <ImageList
                      cols={
                        isMobile ? 2 : (
                          allSelectedImages.length > 4
                            ? 4
                            : allSelectedImages.length
                        )
                      }
                      rowHeight={isMobile ? 150 : 200}
                    >
                      {allSelectedImages.slice(0, 8).map((image, index) => (
                        <ImageListItem key={index}>
                          <img
                            src={image}
                            alt={`미리보기 ${index + 1}`}
                            loading='lazy'
                            style={{ borderRadius: 8 }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                    {allSelectedImages.length > 8 && (
                      <Typography variant='caption' color='text.secondary' sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                        +{allSelectedImages.length - 8}개 이미지 더 있음
                      </Typography>
                    )}
                  </Box>
                )
              );
            })()}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {postContent.hashtags.map((hashtag, index) => (
                <Typography
                  key={index}
                  variant='body2'
                  color='primary'
                  component='span'
                  sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                >
                  {hashtag}{' '}
                </Typography>
              ))}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 설정 다이얼로그 */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: isMobile ? '90vh' : '80vh',
            m: isMobile ? 1 : 2,
          }
        }}
      >
        <DialogTitle>
          <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight='700'>
            ⚙️ 해시태그 및 문구 설정
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            {/* 필수 해시태그 설정 */}
            <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant='subtitle1' fontWeight='600' sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  ⭐ 필수 해시태그 설정
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useRequiredHashtags}
                      onChange={(e) => handleUseRequiredHashtagsToggle(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      필수 해시태그 사용 (AI 생성 시 자동 포함)
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />

                {/* 필수 해시태그 목록 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    설정된 필수 해시태그
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 40 }}>
                    {requiredHashtags.length > 0 ? (
                      requiredHashtags.map((hashtag, index) => (
                        <Chip
                          key={index}
                          label={hashtag}
                          onDelete={() => handleRequiredHashtagRemove(hashtag)}
                          color='primary'
                          variant='outlined'
                          sx={{ 
                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant='caption' color='text.secondary' sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                        설정된 필수 해시태그가 없습니다.
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    size='small'
                    placeholder='#필수해시태그 입력 후 Enter'
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const value = (e.target as HTMLInputElement).value.trim();
                        if (value) {
                          const hashtag = value.startsWith('#')
                            ? value
                            : `#${value}`;
                          handleRequiredHashtagAdd(hashtag);
                          (e.target as HTMLInputElement).value = '';
                        }
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: isMobile ? '0.9rem' : '1rem',
                      },
                    }}
                  />
                </Box>

                <Typography variant='caption' color='text.secondary' sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                  💡 필수 해시태그는 AI 포스팅 생성 시 자동으로 포함되며, 수동으로도 적용할 수 있습니다.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* 전후 고정 문구 설정 */}
            <Accordion sx={{ borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant='subtitle1' fontWeight='600' sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  📝 전후 고정 문구 설정
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Switch
                      checked={usePrefixSuffix}
                      onChange={(e) => setUsePrefixSuffix(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      고정 문구 사용 (AI 생성 시 자동 적용)
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <Typography variant='body2' sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    앞에 추가할 고정 문구
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={prefixText}
                    onChange={(e) => setPrefixText(e.target.value)}
                    placeholder='포스팅 앞에 항상 포함될 문구를 입력하세요...'
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: isMobile ? '0.9rem' : '1rem',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant='body2' sx={{ mb: 1, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    뒤에 추가할 고정 문구
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={suffixText}
                    onChange={(e) => setSuffixText(e.target.value)}
                    placeholder='포스팅 뒤에 항상 포함될 문구를 입력하세요...'
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: isMobile ? '0.9rem' : '1rem',
                      },
                    }}
                  />
                </Box>

                <Typography variant='caption' color='text.secondary' sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                  💡 고정 문구는 AI 포스팅 생성 시 자동으로 앞뒤에 추가됩니다. 예: 매장 정보, 연락처, 이벤트 안내 등
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              handlePrefixSuffixSave();
              setSettingsOpen(false);
            }}
            variant='contained'
            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
          >
            저장 후 닫기
          </Button>
          <Button 
            onClick={() => setSettingsOpen(false)} 
            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogEditor;
